import { Injectable } from '@angular/core';
import {
  HostedPaymentsPadResultComponentPaymentPreviewActions,
  HostedPaymentsReviewPaymentComponentPaymentActions,
  HostedPaymentsSelectPaymentMethodPaymentInfoActions,
} from '../component-actions/hosted-payments-page';
import { BaseEffect } from '../helpers';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  LoadingSpinnerService,
  PaymentInformationErrorHelperService,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  ActiveTenantStore,
  UserNotificationAction,
} from '@arc/arc-shared-frontend';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, exhaustMap, of } from 'rxjs';
import { PaymentRuleStore } from '../payment-rules';
import {
  BtPayPaymentInformationActions,
  selectIsPaymentFailed,
  selectPaymentAuthorizationError,
} from '../btpay';
import { TranslateService } from '@ngx-translate/core';
import {
  PoaPadResultComponentPayInfoActions,
  PoaPadResultComponentPaymentPreviewActions,
  PoaPaymentReceiptComponentActions,
  PoiIntlPaymentReceiptComponentActions,
  PoiPadResultComponentPaymentPreviewActions,
  PoiPaymentReceiptComponentActions,
} from '../component-actions';

@Injectable()
export class PaymentInformationEffects extends BaseEffect {
  constructor(
    private store: Store,
    private paymentInformationErrorHelperService: PaymentInformationErrorHelperService,
    private translate: TranslateService,
    actions$: Actions,
    loadingSpinnerService: LoadingSpinnerService,
  ) {
    super(actions$, loadingSpinnerService);
  }

  displayPaymentPreviewError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          HostedPaymentsSelectPaymentMethodPaymentInfoActions.updatePaymentPreviewError,
          HostedPaymentsReviewPaymentComponentPaymentActions.updatePaymentPreviewPaymentReferenceFailed,
        ),
        filter((action) => action.error != null),
        concatLatestFrom(() => [
          this.store.select(PaymentRuleStore),
          this.store.select(ActiveTenantStore),
        ]),
        exhaustMap(([action, paymentRules, tenant]) => {
          this.paymentInformationErrorHelperService.displayPaymentPreviewErrorMessage(
            action.error,
            paymentRules,
            tenant.tenantName,
          );

          return of();
        }),
      ),
    { dispatch: false },
  );

  displayPaymentFailedError$ = createEffect(() =>
    this.store.select(selectIsPaymentFailed).pipe(
      filter((failed) => failed),
      concatLatestFrom(() => [
        this.store.select(selectPaymentAuthorizationError),
      ]),
      exhaustMap(([, error]) => {
        let message = this.translate.instant(
          'ARC_PAYMENT_OPERATION_FAILED',
        ) as string;

        if (error) {
          message += ` - ${error}`;
        }

        return of(
          new UserNotificationAction({
            type: 'error',
            message,
          }),
        );
      }),
    ),
  );

  displayLoadPaymentPreviewError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.UpdatePaymentPreviewErrorAction,
        PoiPadResultComponentPaymentPreviewActions.loadPaymentPreviewError,
        PoaPadResultComponentPaymentPreviewActions.loadPaymentPreviewError,
        HostedPaymentsPadResultComponentPaymentPreviewActions.loadPaymentPreviewError,
        PoiPaymentReceiptComponentActions.loadPaymentPreviewError,
        PoaPaymentReceiptComponentActions.loadPaymentPreviewError,
        PoiIntlPaymentReceiptComponentActions.loadPaymentPreviewError,
      ),
      exhaustMap(() =>
        of(
          new UserNotificationAction({
            type: 'error',
            message: this.translate.instant(
              'ARC_PAYMENT_FAILED_TO_LOAD_PAYMENT_PREVIEW',
            ) as string,
          }),
        ),
      ),
    ),
  );

  displayGetBuyerAccountsError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BtPayPaymentInformationActions.GetBuyerAccountsErrorAction,
        PoaPadResultComponentPayInfoActions.getBuyerAccountsError,
      ),
      exhaustMap(() =>
        of(
          new UserNotificationAction({
            type: 'error',
            message: this.translate.instant(
              'ARC_PAYMENT_FAILED_TO_LOAD_BUYER_ACCOUNTS',
            ) as string,
          }),
        ),
      ),
    ),
  );
}
